import { Link } from "react-router-dom";
import { StarMark } from "@/components/Logo";
import { Seo } from "@/components/Seo";
import { API } from "@/lib/api";
import { ShieldCheck, FileText, Download, Lock, Trash2 } from "lucide-react";

const SECTIONS = [
  { icon: FileText, title: "What we collect", body: "Account details (name, work email, role, access scope), security data (hashed password and, if you enable 2FA, an encrypted secret + hashed recovery codes), and an audit trail of actions for accountability. Enquiries you submit are kept to respond to you." },
  { icon: Lock, title: "How we protect it", body: "Data is encrypted in transit (TLS); sensitive secrets and integration credentials are encrypted at rest. Tenants are logically isolated on every request, and all state changes are recorded in a tamper-evident audit trail." },
  { icon: ShieldCheck, title: "Your rights", body: "You can access, export, rectify and erase your personal data. Signed-in users can download a machine-readable copy of their data and delete their account at any time under Account → Security." },
  { icon: Trash2, title: "Retention & erasure", body: "Account data is removed or anonymised on erasure. Audit records are retained for security and accountability. We never sell personal data." },
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-ek-canvas" data-testid="privacy-page">
      <Seo title="Privacy & data protection" description="How EKSELENS · ERIO collects, uses and protects personal data — and the data-subject rights available to you." path="/privacy" />

      <header className="sticky top-0 z-40 border-b border-white/10 bg-black">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-2.5">
            <StarMark size={26} className="text-ek-lilac" />
            <span className="font-display text-lg font-bold text-white">EKSELENS <span className="text-ek-lilac">· ERIO</span></span>
          </Link>
          <Link to="/login" data-testid="privacy-signin-link" className="rounded-sm border border-white/15 px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:border-ek-lilac hover:text-ek-lilac">
            Sign in
          </Link>
        </div>
      </header>

      <section className="mx-auto max-w-5xl px-6 py-14">
        <p className="ek-eyebrow text-ek-purple">Privacy &amp; data protection</p>
        <h1 className="ek-heading mt-3 text-4xl sm:text-5xl">Your data, handled with care.</h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-ek-ink/60">
          We process personal data in line with the UK GDPR and EU GDPR. This page summarises what we
          collect and your rights; the full Privacy Notice and our Data Processing Agreement are available below.
        </p>

        <div className="mt-9 flex flex-wrap gap-3">
          <a href={`${API}/legal/privacy-notice`} target="_blank" rel="noreferrer" data-testid="download-privacy-notice" className="ek-btn">
            <Download size={16} /> Full Privacy Notice (PDF)
          </a>
          <a href={`${API}/legal/dpa`} target="_blank" rel="noreferrer" data-testid="download-dpa" className="inline-flex items-center gap-2 rounded-sm border border-black/10 bg-white px-5 py-3 text-sm font-medium text-ek-ink transition-colors hover:border-ek-purple hover:text-ek-purple">
            <FileText size={16} /> Data Processing Agreement (PDF)
          </a>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2">
          {SECTIONS.map((s) => (
            <div key={s.title} data-testid={`privacy-section-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-xl border border-black/5 bg-white p-6">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-ek-purple/10 text-ek-purple">
                <s.icon size={18} />
              </span>
              <h2 className="mt-4 font-display text-base font-semibold text-ek-ink">{s.title}</h2>
              <p className="mt-1.5 text-sm leading-relaxed text-ek-ink/65">{s.body}</p>
            </div>
          ))}
        </div>

        <p className="mt-10 text-sm text-ek-ink/65">
          Questions about your data? Contact our privacy team via the address in the Privacy Notice. You also
          have the right to complain to your supervisory authority (in the UK, the ICO).
        </p>
      </section>

      <footer className="mx-auto max-w-5xl px-6 py-8">
        <p className="text-xs text-ek-ink/65">© {new Date().getFullYear()} Ekselens Consulting · ERIO Programme · UK Patent GB2613313.2</p>
      </footer>
    </div>
  );
}
